// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { BANNER_BACKEND_URL } from "../constants/backend";
import { isBannerDTO } from "../io/hyperify/core/banner/types/BannerEntity";
import { BannerLocation } from "../io/hyperify/core/banner/types/BannerLocation";
import { BannerDTO } from "../io/hyperify/core/banner/types/BannerDTO";
import { HttpService } from "../io/hyperify/core/HttpService";
import { LogService } from "../io/hyperify/core/LogService";
import {
    Observer,
    ObserverCallback,
    ObserverDestructor,
} from "../io/hyperify/core/Observer";
import { QueryParamUtils } from "../io/hyperify/core/QueryParamUtils";
import { WindowLocationService } from "../io/hyperify/frontend/services/WindowLocationService";
import { GeoIpService, GeoIpServiceEvent } from "./GeoIpService";

const LOG = LogService.createLogger('BannerService');

const CHANGE_BANNER_TIMEOUT = 20*1000;

export enum BannerServiceEvent {
    BANNER_INITIALIZED = "BannerServiceEvent:bannerInitialized",
    BANNER_UPDATED = "BannerServiceEvent:bannerUpdated"
}

export class BannerService {

    public static Event = BannerServiceEvent;

    private static _listener          : any | undefined;
    private static _banner            : BannerDTO | undefined;
    private static _observer          : Observer<BannerServiceEvent> = new Observer<BannerServiceEvent>("BannerService");
    private static _geoIpDataListener : any | undefined;
    private static _bannerVisibilityCounter : number = 0;

    public static isInitialized () : boolean {
        return GeoIpService.hasGeoIpData();
    }

    public static initialize () {
        if ( !GeoIpService.hasGeoIpData() ) {
            LOG.debug('Listening GEO_IP_DATA_UPDATED for BANNER_INITIALIZED');
            this._geoIpDataListener = GeoIpService.on(GeoIpServiceEvent.GEO_IP_DATA_UPDATED, () => {
                LOG.debug('Got GEO_IP_DATA_UPDATED, triggering BANNER_INITIALIZED');
                this._observer.triggerEvent(BannerServiceEvent.BANNER_INITIALIZED);
                this._geoIpDataListener();
                this._geoIpDataListener = undefined;
                if (this._bannerVisibilityCounter >= 1) {
                    this.updateBanner();
                }
            });
        } else {
            LOG.debug('Triggering BANNER_INITIALIZED right away');
            this._observer.triggerEvent(BannerServiceEvent.BANNER_INITIALIZED);
            if (this._bannerVisibilityCounter >= 1) {
                this.updateBanner();
            }
        }
    }

    public static start () : void {
        if (this._listener !== undefined) {
            clearInterval(this._listener);
            this._listener = undefined;
        }
        this._listener = setInterval(
            () => this._onChangeBannerInterval(),
            CHANGE_BANNER_TIMEOUT
        );
        this.updateBanner();
    }

    public static stop () : void {
        if (this._listener !== undefined) {
            clearInterval(this._listener);
            this._listener = undefined;
        }
    }

    public static destroy () : void {
        this._observer.destroy();
        if (this._listener !== undefined) {
            clearTimeout(this._listener);
            this._listener = undefined;
        }
    }

    public static on (
        name : BannerServiceEvent,
        callback : ObserverCallback<BannerServiceEvent>
    ) : ObserverDestructor {
        return this._observer.listenEvent(name, callback);
    }

    public static isLoading () : boolean {
        return !GeoIpService.hasGeoIpData();
    }

    /**
     * Trigger banner update from backend
     */
    public static updateBanner () : void {
        this._updateBanner().catch((err) : void => {
            LOG.error(`Failed to update banner: `, err);
        });
    }

    public static getBanner () : BannerDTO | undefined {
        return this._banner;
    }

    /**
     * Trigger banner update from backend
     */
    private static async _updateBanner () : Promise<void> {
        const referer = WindowLocationService.getReferrer();
        const fromFinland = GeoIpService.isFinnishLanguage() || GeoIpService.isFromFinland();
        LOG.debug('_updateBanner: fromFinland: ', fromFinland);
        const location = fromFinland ? BannerLocation.FINLAND : BannerLocation.ANY;
        const dto = await HttpService.getJson(
            `${ BANNER_BACKEND_URL }${ QueryParamUtils.stringifyQueryParams({l: location, ref: referer}) }`
        );
        if (!isBannerDTO(dto)) {
            LOG.error(`Received DTO was not a banner: `, dto);
            return;
        }

        this._banner = dto;
        if (this._observer.hasCallbacks(BannerServiceEvent.BANNER_UPDATED)) {
            this._observer.triggerEvent(BannerServiceEvent.BANNER_UPDATED)
            LOG.debug(`Banner updated.`);
        } else {
            LOG.debug(`Banner updated but nothing was listening events.`);
        }

    }

    private static _onChangeBannerInterval () : void {
        if (this._bannerVisibilityCounter >= 1) {
            this.updateBanner();
        }
    }

    public static increaseBannerVisibility () : void {
        this._bannerVisibilityCounter += 1;
        if (this._bannerVisibilityCounter >= 1 && this._listener === undefined) {
            this.start();
        }
    }

    public static decreaseBannerVisibility () : void {
        this._bannerVisibilityCounter -= 1;
        if (this._bannerVisibilityCounter <= 0 && this._listener !== undefined) {
            this.stop();
        }
    }

}
